/**
 * NewsAttributionFooter — per-article disclaimer + source attribution +
 * takedown email.
 *
 * Single-locale render via useLang() per legal-upl rule. Copyright
 * attribution per IP Law 2005 Art. 25 + Press Law 2016 Art. 38 fair-use.
 */

import { useLang } from '../../use-lang';
import { resolveHotNews } from '../../i18n/hotnews';
import type { NewsAiDisclosure } from '../../lib/news-api';

export interface NewsAttributionFooterProps {
  sourceName: string;
  sourceUrl: string;
  /** Luat AI 2026 Dieu 28 transparency block (omitted when backend has none). */
  aiDisclosure?: NewsAiDisclosure;
}

export function NewsAttributionFooter({
  sourceName,
  sourceUrl,
  aiDisclosure,
}: NewsAttributionFooterProps) {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);

  // attribution_source_label has 2 {source_name} placeholders + 1 {source_url}
  const attributionText = copy.attribution_source_label
    .replaceAll('{source_name}', sourceName)
    .replace('{source_url}', sourceUrl);

  return (
    <footer
      role="contentinfo"
      aria-label={lang === 'en' ? 'Attribution' : 'Nguồn và miễn trừ trách nhiệm'}
      style={footerStyle}
    >
      {aiDisclosure ? (
        <p style={aiDisclosureStyle}>
          {/* generated_by + reviewed_by rendered VERBATIM from backend. */}
          <span>
            <strong>Tạo bởi:</strong> {aiDisclosure.generated_by}
          </span>
          {' - '}
          <span>
            <strong>Biên tập bởi:</strong> {aiDisclosure.reviewed_by}
          </span>
          {aiDisclosure.reviewed_at_iso ? (
            <>
              {' - '}
              <span>
                <strong>Kiểm duyệt lúc:</strong>{' '}
                {formatReviewedAt(aiDisclosure.reviewed_at_iso, lang)}
              </span>
            </>
          ) : null}
        </p>
      ) : null}

      <p style={{ margin: '0 0 10px' }}>{attributionText}</p>

      <p style={{ margin: '0 0 10px' }}>
        {copy.attribution_takedown_label}{' '}
        <a href={`mailto:${copy.attribution_takedown_email}`}>
          {copy.attribution_takedown_email}
        </a>
        {' / '}
        <a href={`mailto:${copy.attribution_takedown_email}?subject=Takedown%20${encodeURIComponent(sourceUrl)}`}>
          {copy.attribution_takedown_form_label}
        </a>
      </p>

      <p style={{ margin: 0, fontStyle: 'italic' }}>{copy.attribution_disclaimer_label}</p>
    </footer>
  );
}

function formatReviewedAt(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const footerStyle: React.CSSProperties = {
  marginTop: 32,
  paddingTop: 16,
  borderTop: '1px solid var(--asklaw-border)',
  fontSize: 12,
  lineHeight: 1.55,
  color: 'var(--asklaw-muted)',
};

const aiDisclosureStyle: React.CSSProperties = {
  margin: '0 0 10px',
  paddingBottom: 10,
  borderBottom: '1px solid var(--asklaw-border)',
  color: 'var(--asklaw-ink-soft)',
};
